import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import { useUpdateRoom } from '../../hooks/chat/useChatRoomActions';
import type { ChatRoom } from '../../types/chat';

interface GroupSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  room: ChatRoom;
}

const GroupSettingsModal: React.FC<GroupSettingsModalProps> = ({ isOpen, onClose, room }) => {
  const [name, setName] = useState(room.name ?? '');
  const [iconUrl, setIconUrl] = useState(room.icon_url ?? '');
  const updateRoom = useUpdateRoom(room.id);

  const handleSave = async () => {
    try {
      await updateRoom.mutateAsync({
        name: name.trim() || null,
        icon_url: iconUrl.trim() || null,
      });
      toast.success('Group updated');
      onClose();
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Failed to update group');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Group settings"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-medium text-text-secondary hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={updateRoom.isPending}
            className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {updateRoom.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      }
    >
      <div className="py-2 space-y-3">
        <label className="block">
          <span className="block text-xs font-medium text-text-secondary mb-1.5">
            Group name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            className="w-full px-3 py-2 rounded-lg border border-border-secondary bg-surface-primary text-sm text-text-primary focus:border-primary-400 focus:outline-none"
            placeholder="Marketing team"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-text-secondary mb-1.5">
            Icon URL (optional)
          </span>
          <input
            type="url"
            value={iconUrl}
            onChange={(e) => setIconUrl(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-border-secondary bg-surface-primary text-sm text-text-primary focus:border-primary-400 focus:outline-none"
            placeholder="https://…"
          />
        </label>
      </div>
    </Modal>
  );
};

export default GroupSettingsModal;
